import FlexibleTable from './FlexibleTable';
import SavedCuratorFiles from './SavedCuratorFiles';
import { useChannelLabel } from '../context/ChannelNumberingContext';
import { hasSossaFields, isSossaDataset } from '../utils/sossa';
import React, { useEffect, useMemo, useState } from 'react';
import apiClient from '../api/client';
import {
  filterActiveClusters,
  normalizeMinimumSpikeCount
} from '../utils/clusterActivity';
import { normalizeCuratorClusterId, normalizeCuratorDatasetTimes } from '../utils/curatorDataset';
import {
  loadCuratorSessionDataset,
  saveCuratorSessionDataset,
} from '../utils/curatorSessionStore';
import {
  CLUSTER_GROUPS,
  getClusterGroup,
  normalizeClusterGroups,
} from '../utils/clusterGroups';
import './CuratorWidget.css';

const KNOWN_CLUSTER_FIELDS = new Set([
  'id',
  'best_match',
  'second_match',
  'num_fs_spikes',
  'clusterId',
  'cluster_id',
  'label',
  'name',
  'spikeTimes',
  'spike_times',
  'times',
  'spikeChannels',
  'spike_channels',
  'channels',
  'points',
  'pcaPoints',
  'pca_points',
  'embedding',
  'waveforms',
  'spikeWaveforms',
  'spike_waveforms',
  'amplitudes',
  'spikeAmplitudes',
  'spike_amplitudes',
  'primaryChannel',
  'primary_channel',
  'channel'
]);

const PRIMARY_CHANNEL_FIELDS = ['primaryChannel', 'primary_channel', 'channel'];

const toNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

const isMissingPrimaryChannel = (value) => (
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0)
);

const isPlainObject = (value) => (
  value !== null &&
  typeof value === 'object' &&
  !Array.isArray(value)
);

const normalizeSpikeTimes = (rawSpikeTimes) => {
  if (!Array.isArray(rawSpikeTimes)) {
    return [];
  }

  return rawSpikeTimes
    .map(toNumber)
    .filter((value) => value !== null);
};

const normalizeNumberArray = (values) => (
  Array.isArray(values)
    ? values.map(toNumber).filter((value) => value !== null)
    : []
);

const normalizePoints = (values) => (
  Array.isArray(values)
    ? values.map((point) => {
        if (Array.isArray(point) && point.length >= 2) {
          const x = toNumber(point[0]);
          const y = toNumber(point[1]);
          return x !== null && y !== null ? [x, y] : null;
        }
        if (isPlainObject(point)) {
          const x = toNumber(point.x ?? point.pc1 ?? point[0]);
          const y = toNumber(point.y ?? point.pc2 ?? point[1]);
          return x !== null && y !== null ? [x, y] : null;
        }
        return null;
      }).filter(Boolean)
    : []
);

const normalizeWaveforms = (values) => (
  Array.isArray(values)
    ? values.map((waveform, index) => {
        const amplitude = Array.isArray(waveform)
          ? normalizeNumberArray(waveform)
          : normalizeNumberArray(
              waveform?.amplitude ?? waveform?.values ?? waveform?.data
            );
        if (amplitude.length === 0) return null;

        const suppliedTimes = normalizeNumberArray(
          waveform?.timePoints ?? waveform?.time_points
        );
        return {
          ...(isPlainObject(waveform) ? waveform : {}),
          amplitude,
          timePoints: suppliedTimes.length === amplitude.length
            ? suppliedTimes
            : amplitude.map((_, pointIndex) => pointIndex),
          spikeIndex: waveform?.spikeIndex ?? waveform?.spike_index ?? index,
        };
      }).filter(Boolean)
    : []
);

const isDisplayableMetadataValue = (value) => {
  if (value === null || value === undefined) {
    return false;
  }

  if (['string', 'number', 'boolean'].includes(typeof value)) {
    return true;
  }

  return Array.isArray(value) && value.length <= 6;
};

const getRawPrimaryChannel = (source) => {
  for (const field of PRIMARY_CHANNEL_FIELDS) {
    if (hasOwn(source, field)) {
      return source[field];
    }
  }

  return null;
};

export const normalizeCluster = (cluster, index) => {
  const source = isPlainObject(cluster) ? cluster : {};
  const rawTimes = source.spikeTimes ?? source.spike_times ?? source.times;
  // SOSSA arrays and waveform spikeIndex use upload order, not chronological order.
  const spikeTimes = hasSossaFields(source)
    ? (Array.isArray(rawTimes) ? rawTimes.map(toNumber) : [])
    : normalizeSpikeTimes(rawTimes);
  const spikeChannels = normalizeNumberArray(
    source.spikeChannels ?? source.spike_channels ?? source.channels
  );
  const points = normalizePoints(
    source.points ?? source.pcaPoints ?? source.pca_points ?? source.embedding
  );
  const waveforms = normalizeWaveforms(
    source.waveforms ?? source.spikeWaveforms ?? source.spike_waveforms
  );
  const spikeAmplitudes = normalizeNumberArray(
    source.spikeAmplitudes ?? source.spike_amplitudes ?? source.amplitudes
  );

  const id = normalizeCuratorClusterId(
    source.id ??
    source.clusterId ??
    source.cluster_id ??
    source.label,
    index
  );

  const rawPrimaryChannel = getRawPrimaryChannel(source);
  const numericPrimaryChannel = toNumber(rawPrimaryChannel);
  const primaryChannel = isMissingPrimaryChannel(rawPrimaryChannel)
    ? null
    : numericPrimaryChannel !== null
    ? numericPrimaryChannel
    : rawPrimaryChannel;

  const metadata = Object.entries(source).reduce((accumulator, [key, value]) => {
    if (!KNOWN_CLUSTER_FIELDS.has(key) && isDisplayableMetadataValue(value)) {
      accumulator[key] = value;
    }
    return accumulator;
  }, { ...(source.metadata || {}) });

  return {
    id,
    primaryChannel,
    primaryChannelSource:
      source.primaryChannelSource ||
      (primaryChannel === null || primaryChannel === undefined ? null : 'provided'),
    spikeTimes,
    spikeChannels,
    points,
    waveforms,
    spikeAmplitudes,
    ...(hasSossaFields(source) ? {
      best_match: source.best_match,
      second_match: source.second_match,
      num_fs_spikes: source.num_fs_spikes,
    } : {}),
    spikeCount: spikeTimes.length,
    metadata
  };
};

const normalizePrimaryChannelsForDataset = (clusters, metadata = {}) => {
  const hasExplicitPrimaryChannels = (
    metadata.primaryChannelsProvided === true ||
    metadata.primary_channels_provided === true ||
    metadata.hasPrimaryChannels === true ||
    metadata.has_primary_channels === true
  );

  if (hasExplicitPrimaryChannels || clusters.length <= 1) {
    return clusters;
  }

  const allProvidedZeros = clusters.every((cluster) => (
    cluster.primaryChannelSource === 'provided' &&
    Number(cluster.primaryChannel) === 0
  ));

  if (!allProvidedZeros) {
    return clusters;
  }

  return clusters.map((cluster) => ({
    ...cluster,
    primaryChannel: null,
    primaryChannelSource: null
  }));
};

export const normalizeDataset = (dataset, fallbackName = 'Cluster file') => {
  const payload = dataset?.data ?? dataset ?? {};
  const metadata = payload.metadata ?? dataset?.metadata ?? {};
  const clustersSource = payload.clusters ?? dataset?.clusters ?? [];
  const clusters = Array.isArray(clustersSource)
    ? clustersSource.map(normalizeCluster)
    : [];
  const name = (
    metadata.algorithmName ||
    metadata.name ||
    payload.algorithmName ||
    dataset?.algorithmName ||
    dataset?.name ||
    fallbackName
  );

  return {
    name,
    algorithmName: payload.algorithmName ?? dataset?.algorithmName ?? metadata.algorithmName,
    metadata,
    clusters: isSossaDataset({ ...dataset, ...payload, name, metadata })
      ? clusters : normalizePrimaryChannelsForDataset(clusters, metadata),
    isLoaded: Boolean(dataset)
  };
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  return String(value);
};

const formatSample = (value) => (
  Number.isFinite(value) ? Math.round(value).toLocaleString() : '-'
);

const getSeriesValues = (series) => {
  if (Array.isArray(series)) {
    return series;
  }

  if (!isPlainObject(series)) {
    return null;
  }

  if (Array.isArray(series.data)) {
    return series.data;
  }

  if (Array.isArray(series.values)) {
    return series.values;
  }

  if (Array.isArray(series.filteredData)) {
    return series.filteredData;
  }

  return null;
};

const getSignalChannelEntries = (signalData) => {
  if (!signalData) {
    return [];
  }

  if (Array.isArray(signalData?.traces)) {
    return signalData.traces
      .map((trace, index) => ({
        channelId: trace?.channel ?? trace?.channelId ?? index,
        values: getSeriesValues(trace),
        startTime: toNumber(trace?.startTime ?? signalData?.startTime ?? 0)
      }))
      .filter((entry) => Array.isArray(entry.values));
  }

  const source = signalData?.data ?? signalData?.channels ?? signalData;

  if (Array.isArray(source)) {
    return source
      .map((series, index) => ({
        channelId: series?.channel ?? series?.channelId ?? index,
        values: getSeriesValues(series),
        startTime: toNumber(series?.startTime ?? signalData?.startTime ?? 0)
      }))
      .filter((entry) => Array.isArray(entry.values));
  }

  if (isPlainObject(source)) {
    return Object.entries(source)
      .map(([channelId, series]) => ({
        channelId: series?.channel ?? series?.channelId ?? channelId,
        values: getSeriesValues(series),
        startTime: toNumber(series?.startTime ?? signalData?.startTime ?? 0)
      }))
      .filter((entry) => Array.isArray(entry.values));
  }

  return [];
};

export const predictPrimaryChannels = (clusters, signalData) => {
  const channels = getSignalChannelEntries(signalData);

  if (!channels.length) {
    return {
      clusters,
      hasSignal: false,
      predictedCount: 0
    };
  }

  let predictedCount = 0;
  const nextClusters = clusters.map((cluster) => {
    if (!isMissingPrimaryChannel(cluster.primaryChannel)) {
      return cluster;
    }

    let bestChannel = null;

    channels.forEach(({ channelId, values, startTime }) => {
      let total = 0;
      let count = 0;

      cluster.spikeTimes.forEach((time) => {
        const sampleIndex = Math.round(Number(time));
        const relativeIndex = Number.isFinite(startTime)
          ? sampleIndex - startTime
          : sampleIndex;

        if (
          !Number.isFinite(sampleIndex) ||
          !Number.isFinite(relativeIndex) ||
          relativeIndex < 0 ||
          relativeIndex >= values.length
        ) {
          return;
        }

        const window = values.slice(Math.max(0, relativeIndex - 15), relativeIndex + 16)
          .filter((value) => value !== null && value !== undefined && value !== '')
          .map(Number).filter(Number.isFinite);
        if (window.length < 2) return;
        total += Math.max(...window) - Math.min(...window);
        count += 1;
      });

      if (!count) {
        return;
      }

      const score = total / count;
      if (!bestChannel || score > bestChannel.score) {
        bestChannel = { channelId, score, tied: false };
      } else if (score === bestChannel.score) {
        bestChannel.tied = true;
      }
    });

    if (!bestChannel || bestChannel.score <= 0 || bestChannel.tied) {
      return cluster;
    }

    predictedCount += 1;
    return {
      ...cluster,
      primaryChannel: bestChannel.channelId,
      primaryChannelSource: 'predicted',
      metadata: {
        ...cluster.metadata,
        predictionScore: Number(bestChannel.score.toFixed(4))
      }
    };
  });

  return {
    clusters: nextClusters,
    hasSignal: true,
    predictedCount
  };
};

const getMissingPrimaryCount = (clusters = []) => (
  clusters.filter((cluster) => isMissingPrimaryChannel(cluster.primaryChannel)).length
);

const formatPredictionMessage = ({
  signalPredictedCount = 0,
  datasetPredictedCount = 0,
  datasetName = '',
  remainingMissing = 0
}) => {
  const parts = [];

  if (signalPredictedCount > 0) {
    parts.push(
      `Predicted ${signalPredictedCount.toLocaleString()} primary channel${signalPredictedCount === 1 ? '' : 's'} from the current signal data`
    );
  }

  if (datasetPredictedCount > 0) {
    parts.push(
      `Predicted ${datasetPredictedCount.toLocaleString()} primary channel${datasetPredictedCount === 1 ? '' : 's'} from the loaded dataset${datasetName ? ` (${datasetName})` : ''}`
    );
  }

  if (!parts.length) {
    return '';
  }

  if (remainingMissing > 0) {
    parts.push(`${remainingMissing.toLocaleString()} cluster${remainingMissing === 1 ? '' : 's'} still missing a primary channel`);
  }

  return `${parts.join('. ')}.`;
};

const SORT_COLUMNS = [
  { key: 'id', label: 'ID' },
  { key: 'primaryChannel', label: 'CH' },
  { key: 'spikeCount', label: 'Spikes' },
  { key: 'firstSpike', label: 'First spike' },
  { key: 'lastSpike', label: 'Last spike' }
];

const getSortValue = (cluster, key) => {
  if (key === 'firstSpike') {
    return cluster.spikeTimes.length ? cluster.spikeTimes.reduce((a, b) => Math.min(a, b)) : null;
  }

  if (key === 'lastSpike') {
    return cluster.spikeTimes.length ? cluster.spikeTimes.reduce((a, b) => Math.max(a, b)) : null;
  }

  return cluster[key];
};

const compareValues = (left, right, direction) => {
  const multiplier = direction === 'asc' ? 1 : -1;
  const leftNumber = toNumber(left);
  const rightNumber = toNumber(right);

  if (leftNumber !== null && rightNumber !== null) {
    return (leftNumber - rightNumber) * multiplier;
  }

  return String(left ?? '').localeCompare(String(right ?? '')) * multiplier;
};

export const getCuratorClusterIds = (clusters = []) => (
  clusters.map((cluster) => cluster.id)
);

const CuratorWidget = ({
  clusterSetData,
  initialDataset,
  signalData,
  datasetInfo,
  selectedClusters = [],
  clusterAnnotations = {},
  groups = CLUSTER_GROUPS,
  onClusterSelect,
  onAnnotationChange,
  onDatasetChange,
  onUploadComplete,
  onSelectedClustersChange,
  onLoadingChange,
  sessionCacheScope = 'default',
}) => {
  const labelChannel = useChannelLabel();
  const [dataset, setDataset] = useState(() => (
    normalizeDataset(initialDataset, initialDataset?.name || 'No file loaded')
  ));
  const [sortConfig, setSortConfig] = useState({ key: 'spikeCount', direction: 'desc' });
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isPredicting, setIsPredicting] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [minimumSpikeCount, setMinimumSpikeCount] = useState(1);
  const [spikeTimeUnit, setSpikeTimeUnit] = useState('auto');
  const availableGroups = useMemo(() => normalizeClusterGroups(
    groups,
    Object.values(clusterAnnotations || {}).map((annotation) => annotation?.group)
  ), [clusterAnnotations, groups]);

  useEffect(() => {
    if (!initialDataset?.metadata?.sourceSignature || !initialDataset?.clusters?.length) return;
    setDataset(normalizeDataset(
      initialDataset,
      initialDataset.name || 'SpikeScope curation session'
    ));
  }, [initialDataset]);

  useEffect(() => {
    onLoadingChange?.(
      'curator',
      isUploading || isPredicting || isRestoring,
      isUploading
        ? 'Loading cluster file…'
        : isRestoring
        ? 'Restoring session file…'
        : 'Predicting primary channels…'
    );
  }, [isPredicting, isRestoring, isUploading, onLoadingChange]);

  useEffect(() => {
    if (initialDataset?.clusters?.length) return undefined;

    let active = true;
    setIsRestoring(true);
    loadCuratorSessionDataset({ scope: sessionCacheScope })
      .then((cachedDataset) => {
        if (!active || !cachedDataset) return;
        setDataset(normalizeDataset(cachedDataset, cachedDataset.name || 'Session file'));
        setNotice(`Restored ${cachedDataset.name || 'the curator file'} from this tab session.`);
        setError('');
      })
      .catch(() => {
        // A cache miss should behave exactly like a fresh curator widget.
      })
      .finally(() => {
        if (active) setIsRestoring(false);
      });

    return () => {
      active = false;
    };
  }, [initialDataset, sessionCacheScope]);

  useEffect(() => {
    if (!clusterSetData) {
      return;
    }

    setDataset(normalizeDataset(clusterSetData, 'Wired cluster set'));
    setError('');
    setNotice('');
  }, [clusterSetData]);

  useEffect(() => {
    if (dataset.isLoaded && typeof onDatasetChange === 'function') {
      const effectiveDataset = spikeTimeUnit === 'auto'
        ? dataset
        : {
            ...dataset,
            metadata: {
              ...(dataset.metadata || {}),
              timeUnit: spikeTimeUnit,
            },
          };
      onDatasetChange(effectiveDataset);
      saveCuratorSessionDataset(effectiveDataset, { scope: sessionCacheScope });
    }
  }, [dataset, onDatasetChange, sessionCacheScope, spikeTimeUnit]);

  const activeClusters = useMemo(() => (
    filterActiveClusters(dataset.clusters, minimumSpikeCount)
  ), [dataset.clusters, minimumSpikeCount]);

  const summary = useMemo(() => {
    const clusters = dataset.clusters || [];
    const totalSpikes = activeClusters.reduce((total, cluster) => total + cluster.spikeCount, 0);
    const missingPrimary = activeClusters.filter(
      (cluster) => isMissingPrimaryChannel(cluster.primaryChannel)
    ).length;

    return {
      totalClusters: clusters.length,
      activeClusters: activeClusters.length,
      filteredClusters: clusters.length - activeClusters.length,
      totalSpikes,
      missingPrimary
    };
  }, [activeClusters, dataset.clusters]);

  const sortedClusters = useMemo(() => {
    return [...activeClusters].sort((left, right) => (
      compareValues(
        getSortValue(left, sortConfig.key),
        getSortValue(right, sortConfig.key),
        sortConfig.direction
      )
    ));
  }, [activeClusters, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setIsUploading(true);
    setError('');
    setNotice('');

    try {
      const response = await apiClient.parseClusterComparisonFile(file, { save: true });
      const uploadedDataset = normalizeDataset(response.data?.dataset, file.name);
      onSelectedClustersChange?.([]);
      setDataset(uploadedDataset);
      onUploadComplete?.(uploadedDataset);
      setNotice('File saved to your storage.');
    } catch (uploadError) {
      setError(uploadError?.message || 'Unable to load the cluster file.');
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handlePredictPrimaryChannels = async () => {
    setIsPredicting(true);
    setError('');
    setNotice('');

    try {
      const predictionInput = normalizeCuratorDatasetTimes({
        ...dataset,
        metadata: { ...dataset.metadata, ...(spikeTimeUnit === 'auto' ? {} : { timeUnit: spikeTimeUnit }) },
        clusters: dataset.clusters.map((cluster) => cluster.primaryChannelSource === 'predicted'
          ? { ...cluster, primaryChannel: null } : cluster),
      }, datasetInfo);
      const signalResult = predictPrimaryChannels(predictionInput.clusters, signalData);
      let nextClusters = signalResult.hasSignal ? signalResult.clusters : predictionInput.clusters;
      let signalPredictedCount = signalResult.predictedCount || 0;
      let datasetPredictedCount = 0;
      let predictionDataset = null;
      let backendAttempted = false;
      let backendErrorMessage = '';

      if (getMissingPrimaryCount(nextClusters) > 0) {
        backendAttempted = true;

        try {
          const response = await apiClient.predictPrimaryChannels(nextClusters);
          const predictedClusters = response.data?.clusters || [];
          datasetPredictedCount = response.data?.predictedCount || 0;
          predictionDataset = response.data?.dataset || null;

          if (predictedClusters.length) {
            nextClusters = predictedClusters.map(normalizeCluster);
          }
        } catch (backendError) {
          backendErrorMessage = backendError?.message || 'Unable to use the loaded dataset for fallback prediction.';

          if (!signalPredictedCount) {
            throw backendError;
          }
        }
      }

      const remainingMissing = getMissingPrimaryCount(nextClusters);
      const totalPredicted = signalPredictedCount + datasetPredictedCount;

      setDataset((current) => ({
        ...current,
        clusters: current.clusters.map((original) => {
          const prediction = nextClusters.find((cluster) => String(cluster.id) === String(original.id));
          if (!prediction || isMissingPrimaryChannel(prediction.primaryChannel)) return original;
          return { ...original, primaryChannel: prediction.primaryChannel,
            primaryChannelSource: prediction.primaryChannelSource,
            metadata: { ...original.metadata, ...prediction.metadata } };
        }),
        metadata: {
          ...(current.metadata || {}),
          predictionDataset: predictionDataset || current.metadata?.predictionDataset
        }
      }));

      if (totalPredicted > 0) {
        setNotice(formatPredictionMessage({
          signalPredictedCount,
          datasetPredictedCount,
          datasetName: predictionDataset,
          remainingMissing
        }));

        if (backendErrorMessage) {
          setError(
            `Applied the current signal-data predictions, but backend fallback failed: ${backendErrorMessage}`
          );
        }
        return;
      }

      if (signalResult.hasSignal && backendAttempted) {
        setError(
          `No missing primary channels could be predicted from the current signal data or the loaded dataset${predictionDataset ? ` (${predictionDataset})` : ''}.`
        );
      } else if (signalResult.hasSignal) {
        setError('No missing primary channels could be predicted from the current signal data.');
      } else {
        setError(
          `No missing primary channels could be predicted from the loaded dataset${predictionDataset ? ` (${predictionDataset})` : ''}.`
        );
      }
    } catch (predictionError) {
      setError(predictionError?.message || 'Unable to predict primary channels.');
    } finally {
      setIsPredicting(false);
    }
  };

  const handleClusterSelect = (cluster, additive = true) => {
    if (typeof onClusterSelect === 'function') {
      onClusterSelect(
        {
          ...cluster,
          datasetName: dataset.name,
          datasetMetadata: dataset.metadata
        },
        { additive }
      );
    }
  };

  return (
    <div className="curator-widget">
      <div className="curator-toolbar">
        <div className="curator-file-control">
          <label htmlFor="curator-cluster-file">Cluster file</label>
          <input
            id="curator-cluster-file"
            type="file"
            accept=".json,.mat"
            disabled={isUploading}
            onChange={handleFileUpload}
          />
        </div>
        <label className="curator-filter-control">
          <span>Minimum spikes</span>
          <input
            type="number"
            min="0"
            step="1"
            value={minimumSpikeCount}
            onChange={(event) => setMinimumSpikeCount(event.target.value)}
            onBlur={() => setMinimumSpikeCount(normalizeMinimumSpikeCount(minimumSpikeCount))}
            title="Hide clusters with fewer spikes than this value"
          />
        </label>
        <label className="curator-filter-control">
          <span>Spike-time unit</span>
          <select
            value={spikeTimeUnit}
            onChange={(event) => setSpikeTimeUnit(event.target.value)}
            title="Choose how spike timestamps in the cluster file should be interpreted"
          >
            <option value="auto">Auto</option>
            <option value="samples">Samples</option>
            <option value="seconds">Seconds</option>
            <option value="milliseconds">Milliseconds</option>
          </select>
        </label>
        <button
          type="button"
          className="curator-action-button"
          onClick={handlePredictPrimaryChannels}
          disabled={!summary.totalClusters || isPredicting || !dataset.clusters.some((cluster) =>
            isMissingPrimaryChannel(cluster.primaryChannel) || cluster.primaryChannelSource === 'predicted')}
        >
          Predict Primary Channels
        </button>
      </div>

      <SavedCuratorFiles refreshKey={dataset} onLoad={(savedDataset, filename) => {
        const loaded = normalizeDataset(savedDataset, filename);
        onSelectedClustersChange?.([]);
        setDataset(loaded);
        onUploadComplete?.(loaded);
        setError('');
        setNotice('Saved file loaded.');
      }} />
      <div className="curator-selection-controls">
        <span>
          {selectedClusters.length.toLocaleString()} selected
        </span>
        <button
          type="button"
          onClick={() => onSelectedClustersChange?.(getCuratorClusterIds(activeClusters))}
          disabled={!activeClusters.length}
        >
          Select all
        </button>
        <button
          type="button"
          onClick={() => onSelectedClustersChange?.([])}
          disabled={!selectedClusters.length}
        >
          Deselect all
        </button>
      </div>

      {error && (
        <div className="curator-message curator-message-error">
          {error}
        </div>
      )}

      {notice && (
        <div className="curator-message curator-message-success">
          {notice}
        </div>
      )}

      {(isUploading || isPredicting) && (
        <div className="curator-message">
          {isUploading ? 'Loading cluster file...' : 'Predicting primary channels...'}
        </div>
      )}

      <div className="curator-summary-grid">
        <div className="curator-summary-card">
          <span>Source</span>
          <strong>{dataset.name}</strong>
        </div>
        <div className="curator-summary-card">
          <span>Active clusters</span>
          <strong title={`${summary.filteredClusters} filtered out`}>
            {summary.activeClusters} / {summary.totalClusters}
          </strong>
        </div>
        <div className="curator-summary-card">
          <span>Active spikes</span>
          <strong>{summary.totalSpikes.toLocaleString()}</strong>
        </div>
        <div className="curator-summary-card">
          <span>Missing primary</span>
          <strong>{summary.missingPrimary}</strong>
        </div>
      </div>

      <div className="curator-table-shell">
        <FlexibleTable exportName="CuratorWidget" className="curator-table">
          <thead>
            <tr>
              <th aria-label="Selected" />
              {SORT_COLUMNS.map((column) => (
                <React.Fragment key={column.key}>
                  <th data-column-id={column.key} data-export-label={column.label}>
                    <button type="button" onClick={() => handleSort(column.key)}>
                      {column.label}
                      {sortConfig.key === column.key && (
                        <span>{sortConfig.direction === 'desc' ? ' down' : ' up'}</span>
                      )}
                    </button>
                  </th>
                  {column.key === 'id' && <th>Group</th>}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {!sortedClusters.length && (
              <tr>
                <td colSpan={7} className="curator-empty-state">
                  {summary.totalClusters
                    ? `No clusters have at least ${normalizeMinimumSpikeCount(minimumSpikeCount).toLocaleString()} spikes.`
                    : 'Load a cluster file to inspect clusters.'}
                </td>
              </tr>
            )}
            {sortedClusters.map((cluster) => {
              const selected = selectedClusters.some(
                (clusterId) => String(clusterId) === String(cluster.id)
              );
              const group = getClusterGroup(clusterAnnotations, cluster.id, availableGroups);

              return (
                <tr
                  key={cluster.id}
                  className={selected ? 'selected' : ''}
                  onClick={() => handleClusterSelect(cluster)}
                  onDoubleClick={() => handleClusterSelect(cluster, false)}
                >
                <td>
                  <input
                    type="checkbox"
                    checked={selected}
                    onClick={(event) => event.stopPropagation()}
                    onDoubleClick={(event) => event.stopPropagation()}
                    onChange={() => handleClusterSelect(cluster)}
                    aria-label={`Select cluster ${cluster.id}`}
                  />
                </td>
                <td>{cluster.id}</td>
                <td>
                  <select
                    className={`curator-group-select group-${group}`}
                    value={group}
                    onClick={(event) => event.stopPropagation()}
                    onDoubleClick={(event) => event.stopPropagation()}
                    onChange={(event) => {
                      event.stopPropagation();
                      onAnnotationChange?.(cluster.id, { group: event.target.value });
                    }}
                    aria-label={`Group for cluster ${cluster.id}`}
                  >
                    {availableGroups.map((groupOption) => (
                      <option key={groupOption} value={groupOption}>
                        {groupOption}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  {formatValue(labelChannel(cluster.primaryChannel))}
                  {cluster.primaryChannelSource === 'predicted' && (
                    <span className="curator-pill">predicted</span>
                  )}
                </td>
                <td>{cluster.spikeCount.toLocaleString()}</td>
                <td>{formatSample(getSortValue(cluster, 'firstSpike'))}</td>
                <td>{formatSample(getSortValue(cluster, 'lastSpike'))}</td>
              </tr>
              );
            })}
          </tbody>
        </FlexibleTable>
      </div>
    </div>
  );
};

export default CuratorWidget;
